import { useEffect } from 'react';
import { isDesktopRuntime } from '@/desktop/desktopDetection';
import { getMachineReportingConsent } from '@/desktop/desktopRuntimeLazy';

/**
 * Starts this machine reporting itself, for as long as the application is running.
 *
 * ⛔ **Mounted at the application root, never inside a view.** Sharing what this machine looks like
 * is something the person switched on for the machine, not for a screen — and while it lived in the
 * Infrastructure page it stopped the moment they went anywhere else, which on a working day is
 * almost always. A teammate's card for this machine then aged into `idle` while the machine was
 * busy, which is the opposite of what the switch promises.
 *
 * Desktop only, because a browser has no machine to read. Consent is read once at start: it is
 * recorded locally, and the switch that changes it tells the reporter directly.
 *
 * The reporter is imported here, not at the top: no browser runs it, so it stays out of the entry
 * every launch parses (T-0559's entry budget).
 */
export function useMachineReporter(): void {
  useEffect(() => {
    if (!isDesktopRuntime()) return;

    let alive = true;
    void Promise.all([import('./machineHistory'), import('./machineReporter')]).then(
      ([{ MACHINE_SAMPLE_INTERVAL_MS }, { setMachineReportingConsent, startMachineReporter }]) => {
        if (!alive) return;
        startMachineReporter(MACHINE_SAMPLE_INTERVAL_MS);
        void getMachineReportingConsent()
          .then((consent) => {
            // Off is the safe reading of "we could not find out", and the true one: consent is
            // stored locally, so a read that fails is a machine that has never said yes.
            if (alive) setMachineReportingConsent(consent?.enabled === true);
          })
          .catch(() => {
            if (alive) setMachineReportingConsent(false);
          });
      },
      // A reporter whose code did not arrive does not report this run; the next launch tries again.
      () => undefined,
    );

    return () => {
      alive = false;
    };
  }, []);
}
